import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import "../../css/MainPage/OnboardingQuestions.css";
import { auth, db } from "../../firebase/firebase";
import { doc, setDoc } from "firebase/firestore";

const OnboardingQuestions = () => {
  const { userLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    location: { zipCode: "", city: "", state: "" },
    educationalStatus: "",
    university: "",
    major: "",
    graduationYear: "",
    careerGoal: "",
    gender: "",
    race: "",
  });

  const questions = [
    {
      id: "name",
      title: "What's your full name?",
      type: "text",
      placeholder: "Enter your full name",
      required: true,
      category: "personal",
    },
    {
      id: "age",
      title: "How old are you?",
      type: "number",
      placeholder: "Enter your age",
      required: true,
      category: "personal",
    },
    {
      id: "zipCode",
      title: "What's your zip code?",
      type: "text",
      placeholder: "Enter your zip code",
      required: true,
      category: "personal",
    },
    {
      id: "educationalStatus",
      title: "What's your current educational status?",
      type: "select",
      options: [
        { value: "", label: "Select your status" },
        { value: "high-school", label: "High School Student" },
        { value: "freshman", label: "College Freshman" },
        { value: "sophomore", label: "College Sophomore" },
        { value: "junior", label: "College Junior" },
        { value: "senior", label: "College Senior" },
        { value: "graduate", label: "Graduate Student" },
        { value: "recent-graduate", label: "Recent Graduate" },
      ],
      required: true,
      category: "academic",
    },
    {
      id: "university",
      title: "What school do you attend? (Optional)",
      type: "text",
      placeholder: "Enter your school/university name",
      required: false,
      category: "academic",
    },
    {
      id: "major",
      title: "What's your field of study or intended major?",
      type: "text",
      placeholder: "Enter your major or field of interest",
      required: true,
      category: "academic",
    },
    {
      id: "graduationYear",
      title: "What's your expected graduation year?",
      type: "text",
      placeholder: "e.g., 2025",
      required: true,
      category: "academic",
    },
    {
      id: "careerGoal",
      title: "What's your dream career or career goal?",
      type: "text",
      placeholder: "Describe your career aspirations",
      required: true,
      category: "academic",
    },
    {
      id: "gender",
      title: "What is your gender? (Optional)",
      type: "select",
      options: [
        { value: "", label: "Prefer not to answer" },
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "non-binary", label: "Non-binary" },
        { value: "other", label: "Other" },
        { value: "prefer-not-to-say", label: "Prefer not to say" },
      ],
      required: false,
      category: "personal",
    },
    {
      id: "race",
      title: "What is your race/ethnicity? (Optional)",
      type: "select",
      options: [
        { value: "", label: "Prefer not to answer" },
        { value: "american-indian", label: "American Indian or Alaska Native" },
        { value: "asian", label: "Asian" },
        { value: "black", label: "Black or African American" },
        { value: "hispanic", label: "Hispanic or Latino" },
        {
          value: "pacific-islander",
          label: "Native Hawaiian or Pacific Islander",
        },
        { value: "white", label: "White" },
        { value: "two-or-more", label: "Two or more races" },
        { value: "prefer-not-to-say", label: "Prefer not to say" },
      ],
      required: false,
      category: "personal",
    },
  ];

  useEffect(() => {
    if (!userLoggedIn) {
      navigate("/login");
    }
  }, [userLoggedIn, navigate]);

  const handleZipCodeChange = async (value) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, zipCode: value },
    }));

    if (value.length === 5) {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${value}`);
        if (response.ok) {
          const data = await response.json();
          const { "place name": city, "state abbreviation": state } =
            data.places[0];
          setFormData((prev) => ({
            ...prev,
            location: { zipCode: value, city, state },
          }));
        }
      } catch (error) {
        console.error("Error fetching location data: ", error);
      }
    }
  };

  const handleInputChange = (value) => {
    const currentQuestion = questions[currentStep];

    if (currentQuestion.id === "zipCode") {
      handleZipCodeChange(value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [currentQuestion.id]: value,
      }));
    }
  };

  const getCurrentValue = () => {
    const currentQuestion = questions[currentStep];
    if (currentQuestion.id === "zipCode") {
      return formData.location.zipCode;
    }
    return formData[currentQuestion.id] || "";
  };

  const isCurrentStepValid = () => {
    const currentQuestion = questions[currentStep];
    const value = getCurrentValue();

    if (currentQuestion.required) {
      return value.trim() !== "";
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No user is logged in");

      // Prepare the data for Firestore
      const userData = {
        ...formData,
        email: currentUser.email,
        profilePicture: "",
        bio: "",
        linkedIn: "",
        demographics: [],
        gpa: "",
        testScores: [{ testName: "", score: "" }],
        activities: [{ activityName: "", description: "" }],
        householdIncome: "",
        gender: "",
        race: "",
        onboardingCompleted: true,
        createdAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "users", currentUser.uid), userData);

      // Navigate to profile page
      navigate("/profile");
    } catch (error) {
      console.error("Error saving user data: ", error);
      setErrorMessage("Failed to save your information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  if (!userLoggedIn) {
    return null;
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-wrapper">
        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Question {currentStep + 1} of {questions.length}
          </div>
        </div>

        {/* Question Card */}
        <div className="question-card">
          <div className="question-header">
            <div className="category-badge">
              {currentQuestion.category === "personal"
                ? "👤 Personal"
                : "🎓 Academic"}
            </div>
            <h2 className="question-title">{currentQuestion.title}</h2>
          </div>

          <div className="question-content">
            {currentQuestion.type === "select" ? (
              <select
                value={getCurrentValue()}
                onChange={(e) => handleInputChange(e.target.value)}
                className="question-select"
              >
                {currentQuestion.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={currentQuestion.type}
                value={getCurrentValue()}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={currentQuestion.placeholder}
                className="question-input"
                autoFocus
              />
            )}

            {/* Location Display */}
            {currentQuestion.id === "zipCode" &&
              formData.location.city &&
              formData.location.state && (
                <div className="location-display">
                  📍 {formData.location.city}, {formData.location.state}
                </div>
              )}
          </div>

          {/* Navigation Buttons */}
          <div className="question-actions">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="btn-secondary"
            >
              ← Previous
            </button>

            <button
              onClick={handleNext}
              disabled={!isCurrentStepValid() || isLoading}
              className="btn-primary"
            >
              {isLoading
                ? "Saving..."
                : currentStep === questions.length - 1
                ? "Complete Setup →"
                : "Next →"}
            </button>
          </div>

          {errorMessage && (
            <div className="error-message">⚠️ {errorMessage}</div>
          )}
        </div>

        {/* Skip Option */}
        <div className="skip-section">
          <button onClick={() => navigate("/profile")} className="skip-button">
            Skip for now - I'll complete this later
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingQuestions;
